import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { Button, Card, Input, Row, Table } from "antd";
import { SorterResult, SortOrder } from "antd/es/table/interface";
import { TableProps } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { message, Modal } from "components";
import { RootState, useAppDispatch } from "app/store";
import AddButton from "components/AddButton";
import ResizableTitle from "components/ResizableTitle";
import useResizableTableHeader from "hooks/useResizableTableHeader";
import useAppAuth0 from "hooks/useAppAuth0";
import { getTotalDisplay } from "utils/getTotalDisplay";
import * as Icons from "assets/icons";
import { EmailDomainListResponse } from "types/dto/response/accessControl";
import { emailDomainSelectors, fetchEmailDomains, removeEmailDomain } from "../accessControlSlice";
import useColumns from "./useColumns";

function AccessControlList() {
  const [query, setQuery] = useQueryParams({
    emailDomain: withDefault(StringParam, ""),
    page: withDefault(NumberParam, 1),
    size: withDefault(NumberParam, 10),
    sortKey: withDefault(StringParam, "lastModifiedDate"),
    order: withDefault(StringParam, "descend"),
  });
  const [searchValue, setSearchValue] = useState<string>(query.emailDomain);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { user } = useAppAuth0();

  const isLoading = useSelector((state: RootState) => state.loading);
  const dataSource = useSelector(emailDomainSelectors.selectAll);
  const { totalElements, currentPage, currentSize } = useSelector((state: RootState) => state.accessControl);

  const onDelete = (record: EmailDomainListResponse) => {
    return Modal.confirm({
      title: "Confirm Domain Deletion",
      content: "Are you sure you want to delete this domain?",
      okText: "Delete",
      onOk: () =>
        dispatch(removeEmailDomain(record.id))
          .then(unwrapResult)
          .then(() => {
            if (dataSource.length === 1) {
              setQuery({ page: query.page > 1 ? query.page - 1 : 1 });
            } else {
              fetchData(query);
            }
          })
          .catch((err) => message.error(err).then(Promise.reject)),
    });
  };

  const onEdit = (record: EmailDomainListResponse) => {
    history.push(`/customers/access-control/${record.id}`);
  };

  const columns = useColumns({
    sortOrder: query.order as SortOrder,
    canUpdateEmailDomain: user.canUpdateEmailDomain,
    canDeleteEmailDomain: user.canDeleteEmailDomain,
    onEdit,
    onDelete,
  });

  const fetchData = useCallback(
    (q: typeof query) =>
      dispatch(fetchEmailDomains({ ...q, order: q.order as SortOrder }))
        .then(unwrapResult)
        .catch(message.error),
    [dispatch]
  );

  useEffect(() => {
    fetchData(query);
    setSearchValue(query.emailDomain);
  }, [dispatch, fetchData, query]);

  const refresh = () => {
    if (query.page === 1) {
      fetchData(query);
    } else {
      setQuery({ page: 1 });
    }
  };

  const handleTableChange: TableProps<EmailDomainListResponse>["onChange"] = (pagination, filters, sorter, extra) => {
    const { order, field } = sorter as SorterResult<EmailDomainListResponse>;
    if (extra.action === "sort" && order && field) {
      setQuery({ sortKey: field as string, order, page: 1 });
    }
  };

  const columnsToDisplay = useResizableTableHeader(columns, 130, 400);

  return (
    <Card
      title="Access Control List"
      extra={user.canCreateEmailDomain && <AddButton text="+ Add domain" />}
      className="page-container"
    >
      <Row justify="space-between" style={{ marginBottom: "16px" }}>
        <Input
          allowClear
          placeholder="Search domain"
          value={searchValue}
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchValue(e.target.value)}
          onBlur={() => setSearchValue((prev) => prev.trim())}
          onPressEnter={() => setQuery({ emailDomain: searchValue.trim(), page: 1 })}
          style={{ width: "25%" }}
        />
        <Button onClick={refresh} type="text" shape="circle" icon={<Icons.Refresh />} />
      </Row>

      <Table<EmailDomainListResponse>
        scroll={{ x: "100%" }}
        loading={{ spinning: isLoading }}
        columns={columnsToDisplay}
        components={{ header: { cell: ResizableTitle } }}
        dataSource={dataSource}
        rowKey={(record) => record.id}
        onChange={handleTableChange}
        pagination={{
          showTotal: getTotalDisplay,
          total: totalElements,
          showSizeChanger: true,
          current: currentPage,
          pageSize: currentSize,
          onChange: (page, pageSize) => setQuery({ page, size: pageSize }),
        }}
      />
    </Card>
  );
}

export default AccessControlList;
